import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Plus, Search, BookOpen, Video, Users, MoreVertical, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function CourseList() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  const fetchCourses = async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 15 };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/api/courses', { params });
      setCourses(res.data.courses || []);
      setTotal(res.data.total || 0);
    } catch (err) { toast.error('Failed to load courses'); }
    setLoading(false);
  };

  useEffect(() => { fetchCourses(); }, []);

  const handleSearch = () => { setPage(1); fetchCourses(1); };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this course and all episodes? This cannot be undone.')) return;
    try {
      await api.delete(`/api/courses/${id}`);
      toast.success('Course deleted');
      fetchCourses(page);
    } catch { toast.error('Failed to delete'); }
  };

  const toggleStatus = async (id, current) => {
    const newStatus = current === 'published' ? 'draft' : 'published';
    try {
      await api.put(`/api/courses/${id}`, { status: newStatus });
      toast.success(`Course ${newStatus}`);
      fetchCourses(page);
    } catch { toast.error('Failed to update'); }
  };

  const getStatusBadge = (status) => {
    if (status === 'published') return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Published</Badge>;
    if (status === 'draft') return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Draft</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };

  const getAccessBadge = (level) => {
    const map = { elite: 'bg-yellow-50 text-yellow-800 border-yellow-200', gold: 'bg-yellow-50 text-yellow-800 border-yellow-200', trial_paid: 'bg-blue-50 text-blue-700 border-blue-200', free_trial: 'bg-gray-50 text-gray-600 border-gray-200', free: 'bg-gray-50 text-gray-600 border-gray-200' };
    const display = level === 'gold' ? 'elite' : level;
    return <Badge variant="outline" className={map[level] || ''} data-testid="access-badge">{display}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="course-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Recorded Courses</h1>
          <p className="text-muted-foreground mt-1">{total} courses</p>
        </div>
        <Button onClick={() => navigate('/admin/courses/create')} data-testid="create-course-btn">
          <Plus className="h-4 w-4 mr-2" />Create Course
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="pt-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search courses..."
                className="pl-9"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                data-testid="search-courses"
              />
            </div>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); fetchCourses(1); }}>
              <SelectTrigger className="w-36" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
          ) : courses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No courses found</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/courses/create')}>Create your first course</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {courses.map(c => (
                <div key={c.id} className="flex items-center justify-between p-4 rounded-xl border hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate(`/admin/courses/${c.id}`)} data-testid={`course-row-${c.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Video className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{c.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusBadge(c.status)}
                        {getAccessBadge(c.access_level)}
                        <Badge variant="outline" className="text-[10px]">{c.course_type}</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">{c.episode_count} episodes</p>
                      <p className="text-xs text-muted-foreground">{c.learner_count} learners</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate(`/admin/courses/${c.id}`); }}>
                          <Pencil className="h-4 w-4 mr-2" />Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); toggleStatus(c.id, c.status); }}>
                          {c.status === 'published' ? <><EyeOff className="h-4 w-4 mr-2" />Unpublish</> : <><Eye className="h-4 w-4 mr-2" />Publish</>}
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive" onClick={e => { e.stopPropagation(); handleDelete(c.id); }}>
                          <Trash2 className="h-4 w-4 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}

          {total > 15 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(page - 1); fetchCourses(page - 1); }}>Previous</Button>
              <span className="text-sm text-muted-foreground self-center">Page {page}</span>
              <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 15)} onClick={() => { setPage(page + 1); fetchCourses(page + 1); }}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
